import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Pen, Eraser, Undo2, Save, X, Sticker } from "lucide-react";

const STICKERS = [
  { key: "redo",   label: "REDO",   fill: "#E53E3E", text: "#FFFFFF" },
  { key: "ok",     label: "OK",     fill: "#0F5E39", text: "#FFFFFF" },
  { key: "check",  label: "\u2713", fill: "#0F5E39", text: "#FFFFFF" },
  { key: "cross",  label: "\u2717", fill: "#E53E3E", text: "#FFFFFF" },
  { key: "warn",   label: "!",      fill: "#C9A24B", text: "#0F1E15" },
];

/**
 * PhotoAnnotator — full-screen modal that lets a user scribble on a photo
 * (highlight defects, draw an arrow, circle an area) with mouse or touch
 * before saving the annotated copy.
 *
 * Emits the URL of the annotated image (a fresh upload — never overwrites
 * the source) so downstream views can reference either the original or the
 * annotated variant.
 *
 * Props:
 *   - open           bool
 *   - imageUrl       string  original photo URL (raw or already stored)
 *   - onClose()      close without saving
 *   - onSaved(url)   called with the new URL after upload
 */
export default function PhotoAnnotator({ open, imageUrl, onClose, onSaved }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const wrapRef = useRef(null);
  const [color, setColor] = useState("#E53E3E");
  const [thickness, setThickness] = useState(4);
  const [tool, setTool] = useState("pen"); // pen | eraser | sticker
  const [sticker, setSticker] = useState(STICKERS[0]);
  const [saving, setSaving] = useState(false);
  const drawing = useRef(false);
  const undoStack = useRef([]); // list of ImageData snapshots
  const last = useRef({ x: 0, y: 0 });

  // Load the source image onto the canvas as soon as the modal opens.
  useEffect(() => {
    if (!open || !imageUrl) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const maxW = Math.min(window.innerWidth - 40, 900);
      const scale = Math.min(1, maxW / img.width);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      undoStack.current = [ctx.getImageData(0, 0, canvas.width, canvas.height)];
      imgRef.current = img;
    };
    img.onerror = () => toast.error("Could not load photo — is it reachable?");
    img.src = imageUrl.startsWith("blob:") || imageUrl.startsWith("http") ? imageUrl : fileUrl(imageUrl);
  }, [open, imageUrl]);

  const positionOf = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const startStroke = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    // Snapshot for Undo before this stroke begins.
    undoStack.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
    if (undoStack.current.length > 25) undoStack.current.shift();
    // Sticker tool: single-tap stamp, no stroke.
    if (tool === "sticker") {
      const p = positionOf(e);
      stampSticker(ctx, p.x, p.y);
      return;
    }
    drawing.current = true;
    last.current = positionOf(e);
  };

  const stampSticker = (ctx, x, y) => {
    const scale = Math.max(1, ctx.canvas.width / 700);
    const w = 88 * scale;
    const h = 36 * scale;
    const radius = 14 * scale;
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    // pill background
    ctx.fillStyle = sticker.fill;
    ctx.beginPath();
    const left = x - w / 2, top = y - h / 2, right = x + w / 2, bottom = y + h / 2;
    ctx.moveTo(left + radius, top);
    ctx.lineTo(right - radius, top);
    ctx.quadraticCurveTo(right, top, right, top + radius);
    ctx.lineTo(right, bottom - radius);
    ctx.quadraticCurveTo(right, bottom, right - radius, bottom);
    ctx.lineTo(left + radius, bottom);
    ctx.quadraticCurveTo(left, bottom, left, bottom - radius);
    ctx.lineTo(left, top + radius);
    ctx.quadraticCurveTo(left, top, left + radius, top);
    ctx.closePath();
    ctx.fill();
    // shadow ring
    ctx.strokeStyle = "rgba(0,0,0,0.15)";
    ctx.lineWidth = 1.5 * scale;
    ctx.stroke();
    // label
    ctx.fillStyle = sticker.text;
    ctx.font = `bold ${20 * scale}px system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(sticker.label, x, y + 1 * scale);
    ctx.restore();
  };
  const moveStroke = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = positionOf(e);
    ctx.strokeStyle = color;
    ctx.lineWidth = thickness;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.globalCompositeOperation = tool === "eraser" ? "destination-out" : "source-over";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const endStroke = () => { drawing.current = false; };

  const undo = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (undoStack.current.length <= 1) return;
    undoStack.current.pop();
    const snap = undoStack.current[undoStack.current.length - 1];
    ctx.putImageData(snap, 0, 0);
  };

  const save = async () => {
    if (!canvasRef.current) return;
    setSaving(true);
    try {
      const blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/webp", 0.9));
      const fd = new FormData();
      fd.append("file", blob, `annotated-${Date.now()}.webp`);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Annotated photo saved");
      onSaved(data.url);
    } catch (e) {
      toast.error("Could not save annotated photo");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="photo-annotator"
    >
      <div
        className="bg-[#FAF7EF] rounded-2xl p-4 max-w-[95vw] max-h-[95vh] overflow-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        ref={wrapRef}
      >
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="font-serif text-lg">Annotate photo</div>
          <div className="flex items-center gap-2 flex-wrap">
            {["#E53E3E", "#0F5E39", "#C9A24B", "#0F1E15", "#FFFFFF"].map((c) => (
              <button
                key={c}
                onClick={() => { setTool("pen"); setColor(c); }}
                className={`h-6 w-6 rounded-full border-2 ${color === c && tool === "pen" ? "border-[#0F5E39]" : "border-transparent"}`}
                style={{ backgroundColor: c }}
                data-testid={`annotate-color-${c}`}
                aria-label={`Colour ${c}`}
              />
            ))}
            <select
              value={thickness}
              onChange={(e) => setThickness(Number(e.target.value))}
              className="text-sm rounded-full border border-[#E4DFCF] px-2 py-1 bg-white"
              data-testid="annotate-thickness"
            >
              {[2, 4, 6, 10].map((n) => <option key={n} value={n}>{n}px</option>)}
            </select>
            <button
              onClick={() => setTool(tool === "eraser" ? "pen" : "eraser")}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm ${tool === "eraser" ? "bg-[#0F5E39] text-white border-[#0F5E39]" : "bg-white text-[#0F1E15] border-[#E4DFCF]"}`}
              data-testid="annotate-eraser"
            ><Eraser size={12}/> Eraser</button>
            <button
              onClick={() => setTool("sticker")}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm ${tool === "sticker" ? "bg-[#0F5E39] text-white border-[#0F5E39]" : "bg-white text-[#0F1E15] border-[#E4DFCF]"}`}
              data-testid="annotate-sticker-mode"
            ><Sticker size={12}/> Stickers</button>
            <button
              onClick={undo}
              className="inline-flex items-center gap-1 rounded-full border border-[#E4DFCF] bg-white px-3 py-1 text-sm text-[#0F1E15]"
              data-testid="annotate-undo"
            ><Undo2 size={12}/> Undo</button>
          </div>
        </div>
        <div className="rounded-xl overflow-hidden border border-[#E4DFCF] bg-white flex items-center justify-center">
          <canvas
            ref={canvasRef}
            className={`touch-none max-w-full block ${tool === "sticker" ? "cursor-crosshair" : ""}`}
            onMouseDown={startStroke}
            onMouseMove={moveStroke}
            onMouseUp={endStroke}
            onMouseLeave={endStroke}
            onTouchStart={startStroke}
            onTouchMove={moveStroke}
            onTouchEnd={endStroke}
            data-testid="annotate-canvas"
          />
        </div>
        {tool === "sticker" && (
          <div
            className="mt-3 rounded-xl bg-white border border-[#E4DFCF] p-2 flex flex-wrap items-center gap-2"
            data-testid="sticker-palette"
          >
            <span className="text-[10px] uppercase tracking-widest text-[#4A5A50] mr-1">
              Tap image to place
            </span>
            {STICKERS.map((s) => (
              <button
                key={s.key}
                onClick={() => setSticker(s)}
                className={`rounded-full px-3 py-1 text-xs font-bold border-2 ${sticker.key === s.key ? "border-[#0F1E15]" : "border-transparent"}`}
                style={{ backgroundColor: s.fill, color: s.text }}
                data-testid={`sticker-${s.key}`}
              >{s.label}</button>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onClose}
            className="rounded-full border border-[#E4DFCF] bg-white px-4 py-1.5 text-sm text-[#0F1E15] inline-flex items-center gap-1"
          ><X size={12}/> Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white px-4 py-1.5 text-sm inline-flex items-center gap-1"
            data-testid="annotate-save"
          ><Save size={12}/> {saving ? "Saving…" : "Save annotated"}</button>
        </div>
      </div>
    </div>
  );
}
